<template>
  <!-- Only card customers have anything here: everyone else is invoiced by us
       through the request card above. -->
  <div v-if="isStripeCustomer" class="billing-invoices-card">
    <div class="bic-head">
      <div class="bic-icon">
        <font-awesome-icon icon="fa-solid fa-receipt" />
      </div>
      <div class="bic-head-text">
        <h3>{{ $t('billingInvoices.title') }}</h3>
        <p>{{ $t('billingInvoices.subtitle') }}</p>
      </div>
    </div>

    <!-- Fetched when asked for, not on every visit to the page: each load is a
         call to Stripe, and most visits here are about the plan, not the paperwork. -->
    <button v-if="!loaded && !loading" type="button" class="bic-primary" @click="load">
      <font-awesome-icon icon="fa-solid fa-file-invoice" />
      {{ $t('billingInvoices.show') }}
    </button>

    <div v-if="loading" class="bic-loading">
      <span class="bic-spinner"></span>
      {{ $t('billingInvoices.loading') }}
    </div>

    <p v-if="error" class="bic-error">{{ error }}</p>

    <template v-if="loaded && !loading">
      <p v-if="!invoices.length" class="bic-empty">{{ $t('billingInvoices.empty') }}</p>

      <div v-else class="bic-list">
        <div v-for="invoice in invoices" :key="invoice.id" class="bic-row">
          <span class="bic-date">{{ formatDate(invoice.created_at) }}</span>
          <span class="bic-number">{{ invoice.number || '—' }}</span>
          <span class="bic-amount">{{ formatAmount(invoice.total, invoice.currency) }}</span>
          <span class="bic-status" :class="`is-${invoice.status}`">{{ statusLabel(invoice.status) }}</span>
          <span class="bic-links">
            <a v-if="invoice.pdf_url" :href="invoice.pdf_url" target="_blank" rel="noopener" class="bic-link">
              <font-awesome-icon icon="fa-solid fa-download" />
              {{ $t('billingInvoices.download') }}
            </a>
            <a v-if="invoice.hosted_url" :href="invoice.hosted_url" target="_blank" rel="noopener" class="bic-link bic-link-quiet">
              {{ $t('billingInvoices.view') }}
            </a>
          </span>
        </div>
      </div>

      <!-- Stripe pages its list; the portal is where the rest of the history lives. -->
      <p v-if="hasMore" class="bic-more">{{ $t('billingInvoices.hasMore') }}</p>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/utils/api';
import { useAuthStore } from '@/stores/auth';

const { t, locale } = useI18n();
const authStore = useAuthStore();

const invoices = ref([]);
const loading = ref(false);
const loaded = ref(false);
const hasMore = ref(false);
const error = ref('');

const isStripeCustomer = computed(() => Boolean(authStore.user?.stripe_customer_id));

const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    const response = await api.get('/api/subscription/invoices');
    invoices.value = response.data.invoices || [];
    hasMore.value = Boolean(response.data.has_more);
    loaded.value = true;
  } catch (e) {
    error.value = e.response?.data?.message || t('billingInvoices.loadFailed');
  } finally {
    loading.value = false;
  }
};

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString(locale.value, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const formatAmount = (amount, currency) => {
  const value = Number(amount) || 0;
  try {
    return new Intl.NumberFormat(locale.value, {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(value);
  } catch (e) {
    return `${value.toFixed(2)} ${currency || ''}`.trim();
  }
};

const statusLabel = (status) => {
  const key = `billingInvoices.status.${status}`;
  const label = t(key);
  return label === key ? status : label;
};
</script>

<style scoped>
/* Same shell as .invoice-request-card and .current-plan-card so the three
   stack as one column. */
.billing-invoices-card {
  width: 100%;
  max-width: 1004px;
  margin: 0 auto 40px;
  background: #fff;
  border: 1px solid rgba(31, 41, 51, 0.08);
  border-radius: 24px;
  padding: 28px 34px;
  box-shadow: 0 26px 60px -40px rgba(31, 41, 51, 0.3);
}

.bic-head { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 18px; }

.bic-icon {
  width: 42px; height: 42px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 12px; color: #fff; font-size: 17px;
  background: linear-gradient(150deg, #5aa2ff, #2b82f0);
}

.bic-head-text h3 { margin: 0 0 4px; font-size: 17px; font-weight: 700; color: #111827; }
.bic-head-text p { margin: 0; font-size: 13.5px; color: #6b7280; line-height: 1.5; }

.bic-primary {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 10px 20px; border-radius: 10px; border: 1px solid transparent;
  font-size: 14px; font-weight: 600; cursor: pointer;
  background: #2b82f0; color: #fff;
}
.bic-primary:hover { background: #1a6fdc; }

.bic-loading { display: flex; align-items: center; gap: 10px; font-size: 13.5px; color: #6b7280; }

.bic-spinner {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid #d1d5db; border-top-color: #2b82f0;
  animation: bic-spin 0.7s linear infinite;
}
@keyframes bic-spin { to { transform: rotate(360deg); } }

.bic-error { margin: 10px 0 0; font-size: 13px; color: #b91c1c; }
.bic-empty { margin: 0; font-size: 13.5px; color: #6b7280; }

.bic-list { display: flex; flex-direction: column; }

.bic-row {
  display: grid;
  grid-template-columns: 120px 1fr auto 96px auto;
  align-items: center; gap: 14px;
  padding: 12px 0;
  border-top: 1px solid #f1f3f5;
  font-size: 13.5px;
}
.bic-row:first-child { border-top: none; }

.bic-date { color: #6b7280; }
.bic-number { color: #111827; font-weight: 600; }
.bic-amount { color: #111827; font-weight: 600; white-space: nowrap; }

.bic-status {
  justify-self: start;
  padding: 3px 10px; border-radius: 999px;
  font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em;
  background: #f3f4f6; color: #4b5563;
}
.bic-status.is-paid { background: #dcfce7; color: #166534; }
.bic-status.is-open { background: #fef3c7; color: #92400e; }
.bic-status.is-uncollectible,
.bic-status.is-void { background: #fee2e2; color: #991b1b; }

.bic-links { display: inline-flex; align-items: center; gap: 14px; white-space: nowrap; }

.bic-link {
  display: inline-flex; align-items: center; gap: 6px;
  color: #2b82f0; font-weight: 600; text-decoration: none;
}
.bic-link:hover { text-decoration: underline; }
.bic-link-quiet { color: #6b7280; font-weight: 500; }

.bic-more { margin: 14px 0 0; font-size: 12.5px; color: #6b7280; }

@media (max-width: 768px) {
  .billing-invoices-card { padding: 22px 20px; }
  .bic-row { grid-template-columns: 1fr auto; row-gap: 6px; }
  .bic-links { grid-column: 1 / -1; }
}
</style>
